/**
 * Pantalla del **Optimizador de Cortes** (`/herramientas/optimizador`). Reemplaza el
 * `PlaceholderPage` de `ROUTES.cutlist`. Equivale a `cutlist.html` + `cutlist.js` del
 * SSR (demolidos en esta feature).
 *
 * Orquesta el flujo completo del SSR sobre la API REST:
 *  - Edición de la entrada (piezas, tablero, configuración) en estado local de UI.
 *  - **Optimizar**: corre `optimize()` en el navegador (el servidor no calcula).
 *  - **Guardar**: persiste el snapshot vía `POST /api/v1/cutlist/jobs` (modal Zod).
 *  - **Historial**: lista y recarga jobs vía la API, filtrados por la sede activa.
 *
 * El cálculo es UI puramente local (no es dato de servidor): el resultado y el estado
 * del editor viven en `useState`; solo el historial (guardar/listar/recargar) es dato de
 * servidor con TanStack Query (`docs/frontend_conventions.md`, Datos). Exige
 * `use_cutlist` vía `RequireCutlistPermission` (paridad con el menú/SSR/API).
 */
import { useCallback, useMemo, useState } from "react";

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui";

import {
  INITIAL_CONFIG,
  INITIAL_PARTS,
  INITIAL_STOCK,
  PART_COLORS,
  type StockPreset,
} from "../constants";
import { optimize } from "../optimizer";
import type { CutConfig, CutlistJob, OptimizeResult, Part, Stock } from "../types";
import { ConfigEditor } from "./ConfigEditor";
import { HistoryPanel } from "./HistoryPanel";
import { PartsEditor } from "./PartsEditor";
import { RequireCutlistPermission } from "./RequireCutlistPermission";
import { ResultViewer } from "./ResultViewer";
import { SaveJobDialog } from "./SaveJobDialog";
import { StockEditor } from "./StockEditor";

/** Siguiente id local para una pieza nueva (no persiste; identifica la fila). */
let nextPartId = 1000;

function CutlistEditor() {
  const [parts, setParts] = useState<Part[]>(() =>
    INITIAL_PARTS.map((p) => ({ ...p })),
  );
  const [stock, setStock] = useState<Stock>(() => ({ ...INITIAL_STOCK }));
  const [config, setConfig] = useState<CutConfig>(() => ({ ...INITIAL_CONFIG }));
  const [forcedPresetId, setForcedPresetId] = useState<StockPreset["id"] | null>(null);

  const [result, setResult] = useState<OptimizeResult | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [sheetIdx, setSheetIdx] = useState(0);
  const [zoom, setZoom] = useState(1.0);
  const [saveOpen, setSaveOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // ─── Mutadores de piezas ───────────────────────────────────────────────────
  const updatePart = useCallback((id: number, patch: Partial<Part>) => {
    setParts((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  }, []);

  const addPart = useCallback(() => {
    setParts((prev) => {
      const id = ++nextPartId;
      const color = PART_COLORS[prev.length % PART_COLORS.length];
      return [
        ...prev,
        {
          id,
          label: `Pieza ${String.fromCharCode(64 + prev.length + 1)}`,
          width: 40,
          height: 30,
          quantity: 1,
          canRotate: true,
          material: "",
          color,
        },
      ];
    });
  }, []);

  const removePart = useCallback((id: number) => {
    setParts((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const duplicatePart = useCallback((id: number) => {
    setParts((prev) => {
      const src = prev.find((p) => p.id === id);
      if (!src) return prev;
      const newId = ++nextPartId;
      const color = PART_COLORS[prev.length % PART_COLORS.length];
      const idx = prev.indexOf(src);
      const copy: Part = { ...src, id: newId, label: `${src.label} (2)`, color };
      const next = [...prev];
      next.splice(idx + 1, 0, copy);
      return next;
    });
  }, []);

  // ─── Mutadores de tablero / config ───────────────────────────────────────────
  const updateStock = useCallback((patch: Partial<Stock>) => {
    setStock((prev) => ({ ...prev, ...patch }));
  }, []);

  const selectPreset = useCallback((preset: StockPreset) => {
    setForcedPresetId(preset.id);
    if (preset.w && preset.h) {
      setStock((prev) => ({ ...prev, width: preset.w as number, height: preset.h as number, label: preset.label }));
    }
  }, []);

  const updateConfig = useCallback((patch: Partial<CutConfig>) => {
    setConfig((prev) => ({ ...prev, ...patch }));
  }, []);

  // ─── Cálculo (en el navegador) ───────────────────────────────────────────────
  const canCalc = !isCalculating && parts.length > 0;
  const hasResults = !!(result && result.sheets && result.sheets.length);

  const handleCalculate = useCallback(() => {
    if (isCalculating || parts.length === 0) return;
    setIsCalculating(true);
    setErrorMsg(null);
    // Cede el hilo para que el spinner se pinte antes del cálculo (como el SSR).
    setTimeout(() => {
      try {
        const computed = optimize(parts, [stock], config);
        setResult(computed);
        setSheetIdx(0);
        setZoom(1.0);
        if (!computed || computed.sheets.length === 0) {
          setErrorMsg("No se pudo distribuir ninguna pieza en el tablero.");
        }
      } catch (err) {
        setErrorMsg(
          err instanceof Error ? err.message : "Error al optimizar.",
        );
        setResult(null);
      } finally {
        setIsCalculating(false);
      }
    }, 10);
  }, [isCalculating, parts, stock, config]);

  const clearResults = useCallback(() => {
    setResult(null);
    setErrorMsg(null);
  }, []);

  // ─── Recargar un job del historial ───────────────────────────────────────────
  const handleReloadJob = useCallback((job: CutlistJob) => {
    if (Array.isArray(job.parts)) setParts(job.parts);
    if (Array.isArray(job.stocks) && job.stocks[0]) setStock(job.stocks[0]);
    if (job.config) setConfig((prev) => ({ ...prev, ...job.config }));
    setResult(job.result ?? null);
    setSheetIdx(0);
    setZoom(1.0);
    setForcedPresetId(null);
    setErrorMsg(null);
  }, []);

  const snapshot = useMemo(
    () => ({ parts, stocks: [stock], config, result: result as OptimizeResult }),
    [parts, stock, config, result],
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Encabezado de página + barra de acciones (réplica del SSR). */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Optimizador de cortes</h1>
          <p className="text-sm text-slate-500">
            Distribuye tus piezas en los tableros y minimiza el desperdicio.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="primary" disabled={!canCalc} onClick={handleCalculate}>
            {isCalculating ? "Optimizando…" : "Optimizar"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={!hasResults}
            onClick={() => setSaveOpen(true)}
          >
            Guardar
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={!hasResults}
            onClick={clearResults}
          >
            Limpiar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,540px)_1fr]">
        {/* Columna de entradas */}
        <div className="flex flex-col gap-4">
          <Card>
            <CardHeader>
              <CardTitle>Piezas a cortar</CardTitle>
            </CardHeader>
            <CardContent>
              <PartsEditor
                parts={parts}
                onUpdate={updatePart}
                onAdd={addPart}
                onRemove={removePart}
                onDuplicate={duplicatePart}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Tableros disponibles</CardTitle>
            </CardHeader>
            <CardContent>
              <StockEditor
                stock={stock}
                parts={parts}
                forcedPresetId={forcedPresetId}
                onSelectPreset={selectPreset}
                onUpdateStock={updateStock}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Configuración</CardTitle>
            </CardHeader>
            <CardContent>
              <ConfigEditor config={config} onUpdate={updateConfig} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Historial guardado</CardTitle>
            </CardHeader>
            <CardContent>
              <HistoryPanel onReload={handleReloadJob} />
            </CardContent>
          </Card>
        </div>

        {/* Columna de resultado */}
        <Card>
          <CardHeader>
            <CardTitle>Resultado</CardTitle>
          </CardHeader>
          <CardContent>
            {errorMsg ? (
              <p role="alert" className="mb-3 text-sm text-red-600">
                {errorMsg}
              </p>
            ) : null}
            {hasResults && result ? (
              <ResultViewer
                result={result}
                sheetIdx={sheetIdx}
                zoom={zoom}
                onSelectSheet={(idx) => {
                  setSheetIdx(idx);
                  setZoom(1.0);
                }}
                onPrevSheet={() => {
                  setSheetIdx((i) => Math.max(0, i - 1));
                  setZoom(1.0);
                }}
                onNextSheet={() => {
                  setSheetIdx((i) => Math.min(result.sheets.length - 1, i + 1));
                  setZoom(1.0);
                }}
                onZoomIn={() => setZoom((z) => Math.min(z * 1.3, 8))}
                onZoomOut={() => setZoom((z) => Math.max(z / 1.3, 0.08))}
                onZoomReset={() => setZoom(1.0)}
              />
            ) : (
              <div className="flex flex-col items-center gap-3 py-12 text-center">
                <h2 className="text-lg font-semibold text-slate-700">
                  Sin resultados todavía
                </h2>
                <p className="max-w-md text-sm text-slate-500">
                  Ingresa las piezas a cortar y el tablero disponible, luego presiona{" "}
                  <strong>Optimizar</strong>.
                </p>
                <Button type="button" variant="primary" disabled={!canCalc} onClick={handleCalculate}>
                  {isCalculating ? "Optimizando…" : "Optimizar ahora"}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {hasResults && result ? (
        <SaveJobDialog
          open={saveOpen}
          onClose={() => setSaveOpen(false)}
          snapshot={snapshot}
          onSaved={() => {
            /* La invalidación del historial la hace el hook de guardado. */
          }}
        />
      ) : null}
    </div>
  );
}

/** Pantalla del optimizador con el gating de permiso `use_cutlist`. */
export function CutlistScreen() {
  return (
    <RequireCutlistPermission>
      <CutlistEditor />
    </RequireCutlistPermission>
  );
}
